import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Send, Smartphone } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Card, Input, Spinner, Textarea } from '../../../components/ui'

export default function PushNotificationsPage() {
  const navigate = useNavigate()
  const [loadingStats, setLoadingStats] = useState(true)
  const [deviceCount, setDeviceCount] = useState(0)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => { loadStats() }, [])

  async function loadStats() {
    setLoadingStats(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/notifications/stats'))
      setDeviceCount(res.data.data?.device_count ?? 0)
    } catch (err: any) {
      console.error(err)
    } finally {
      setLoadingStats(false)
    }
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault()
    if (sending || !title.trim() || !body.trim()) return
    if (!confirm(`Enviar push para ${deviceCount} dispositivo(s)?`)) return

    setSending(true)
    try {
      const res = await axios.post(buildApiUrl('/v1/notifications/send'), {
        title: title.trim(),
        body: body.trim(),
      })
      const r = res.data.data
      toast('success', `Enviado: ${r.sent} sucesso, ${r.failed} falha`)
      setTitle('')
      setBody('')
      loadStats()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro ao enviar')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/settings/general')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notificações Push</h1>
          <p className="text-gray-500 mt-1">Envie um aviso pra todos os apoiadores que têm o app instalado</p>
        </div>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-brand-500" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Dispositivos ativos</p>
            <p className="text-2xl font-bold text-gray-900">
              {loadingStats ? '...' : deviceCount}
            </p>
          </div>
        </div>

        <form onSubmit={handleSend} className="space-y-4">
          <Input
            label="Título *"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={120}
            placeholder="Ex: Novo evento na agenda"
            required
            hint={`${title.length}/120`}
          />
          <Textarea
            label="Mensagem *"
            value={body}
            onChange={e => setBody(e.target.value)}
            maxLength={300}
            rows={4}
            placeholder="Conteúdo da notificação que aparece pro apoiador"
            required
            hint={`${body.length}/300`}
          />
          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              loading={sending}
              disabled={!title.trim() || !body.trim() || deviceCount === 0}
            >
              <Send className="w-4 h-4 mr-2" />
              Enviar para {deviceCount} dispositivo{deviceCount === 1 ? '' : 's'}
            </Button>
          </div>
        </form>
      </Card>

      {deviceCount === 0 && !loadingStats && (
        <Card className="p-6 bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-900">
            <strong>Nenhum dispositivo registrado ainda.</strong> Os apoiadores
            precisam instalar o app, fazer login e autorizar notificações pra
            aparecerem aqui.
          </p>
        </Card>
      )}
    </div>
  )
}
